import React, { useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import useExcelUpload from 'hooks/useExcelUpload';

import Table from 'components/common/Table';
import IconButton from 'components/common/IconButton';
import VendorItemsEditModal from 'components/purchase-order/vendor/VendorItemsEditModal';

import { vendorApi, bulkActionApi } from 'api';

import {
  VENDOR_ITEM_LIST_COLUMNS,
  VENDOR_ITEM_FILTER_OPTIONS,
} from 'constants/vendor';

const VendorItems = () => {
  const { vendorId } = useParams();

  const tableRef = useRef();
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const { selectedWarehouse } = useUserContext();

  // Vendor Item Upload
  const handleVendorItemUpload = async file => {
    try {
      const formData = new FormData();

      formData.append('action_type', 'vendor_product_create');
      formData.append('warehouse_id', selectedWarehouse?.id);
      formData.append('file', file);

      await bulkActionApi.bulkFileUpload(formData);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('File upload failed. Please try again.');
    } finally {
      resetFile();
    }
  };

  const { fileInputRef, handleFileSelect, handleFileChange, resetFile } =
    useExcelUpload({
      onFileSelect: handleVendorItemUpload,
    });

  // Vendor Item Template Download
  const handleVendorItemTemplateDownload = () => {
    const downloadUrl = bulkActionApi.getTemplateDownloadUrl(
      'vendor_product_create',
    );

    window.open(downloadUrl, '_blank');
    toast.success('Successfully downloaded template.');
  };

  const handleRemoveVendorProducts = useCallback(async selectedRows => {
    const vendorProductIds = selectedRows.map(row => row.id);

    const response = await vendorApi.deleteVendorProducts({
      ids: vendorProductIds,
    });

    return {
      result: response,
      shouldRefetch: true,
    };
  }, []);

  const handleEditVendorProducts = useCallback(async selectedRows => {
    setSelectedItems(selectedRows);
    setShowEditModal(true);

    return { shouldRefetch: false };
  }, []);

  const handleModalClose = useCallback(() => {
    setShowEditModal(false);
    setSelectedItems([]);
  }, []);

  const handleSaveEdits = useCallback(async updatedItems => {
    try {
      await vendorApi.updateVendorProducts(updatedItems);

      tableRef.current?.refetch();

      toast.success('Successfully updated items');
    } catch (error) {
      toast.error('Fail to update items');
    }
  }, []);

  const headerActions = (
    <>
      <div>
        <input
          type="file"
          ref={fileInputRef}
          className="d-none"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
        />
        <IconButton
          variant="primary"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={handleFileSelect}
        >
          <span className="d-none d-sm-inline-block">Import Excel</span>
        </IconButton>
      </div>

      <IconButton
        variant="falcon-default"
        size="sm"
        icon="arrow-down"
        transform="shrink-3"
        onClick={handleVendorItemTemplateDownload}
      >
        <span className="d-none d-sm-inline-block">Download Template</span>
      </IconButton>
    </>
  );

  const exportApi = useCallback(
    params => {
      return vendorApi.exportVendorProducts(vendorId, params);
    },
    [vendorId],
  );

  const bulkActions = [
    {
      value: 'remove_vendor_products',
      label: 'Remove',
      variant: 'falcon-default',
      className: 'text-danger',
      successMessage: 'Successfully remove items',
      errorMessage: 'Fail to remove items',
      handler: handleRemoveVendorProducts,
    },
    {
      value: 'edit_vendor_products',
      label: 'Edit',
      variant: 'falcon-default',
      skipSuccessToast: true,
      errorMessage: 'Fail to load edit items',
      handler: handleEditVendorProducts,
    },
  ];

  return (
    <>
      <Table
        ref={tableRef}
        title="Vendor List Item"
        apiService={vendorApi}
        sortable={true}
        columns={VENDOR_ITEM_LIST_COLUMNS}
        filterOptions={VENDOR_ITEM_FILTER_OPTIONS}
        fetchConfig={{
          method: 'getProductsOfVendor',
          enablePagination: true,
        }}
        resourceId={vendorId}
        exportApi={exportApi}
        bulkActions={bulkActions}
        headerActions={headerActions}
      />

      <VendorItemsEditModal
        show={showEditModal}
        onHide={handleModalClose}
        selectedItems={selectedItems}
        onSave={handleSaveEdits}
      />
    </>
  );
};

export default VendorItems;
